import { Paper, Typography } from '@material-ui/core';

import PropTypes from 'prop-types';
import React, { useContext } from 'react';

import CrudTableCard from 'modules/common/crudTableCard.component';
import { enumColumn, getEnumColumns } from 'modules/common/enumTableColumn';
import InputContext from 'modules/intake/form/inputContext';
import {
  FormatPersonName,
  vendorContactSearchInfo,
  vendorSearchInfo,
} from 'modules/authorizations/details/global/DestServices/formData/formDataConstants';
import { formatDateForInput } from 'utilities/common';

const SERVICE_TABS = [
  { header: 'Destination Service', order: 1, field: 'destinationServices', iconPath: ['fas', 'user'] },
  { header: 'Language Training', order: 2, field: 'languageTraining', iconPath: ['fas', 'user'] },
  { header: 'International Schooling', order: 3, field: 'internationalSchooling', iconPath: ['fas', 'user'] },
];

const GlobalDestinationCard = ({ globalService, contacts, vendors: vendorOptions, vendorContacts: contactOptions, enumerations, setFieldValue, isSubmitting, isReadOnly }) => {
  const inputContext = useContext(InputContext);
  const vendors = Array.isArray(vendorOptions) ? vendorOptions : [];
  const vendorContacts = Array.isArray(contactOptions) ? contactOptions : [];
  const familyMembers = contacts.filter((contact) => !!contact.id).map((contact) => ({
    id: contact.id,
    title: `${contact.firstName} ${contact.lastName}`.trim(),
    description: `${contact.firstName} ${contact.lastName}`.trim(),
  }));

  const getFamilyMemberName = (contactId) =>
    familyMembers.find((contact) => contact.id === Number(contactId))?.title || '';

  const schoolingLevels = (enumerations.schoolingLevels || []).filter((option) => option.isActive === true);
  const { schoolingLevelColumn } = getEnumColumns(enumerations);
  const contactSearchInfo = vendorContactSearchInfo(vendorContacts);
  const vendorColumn = (header) => ({
    ...enumColumn('vendorId', vendors, { valueKey: 'vendorId', label: 'companyName' }),
    header,
    type: 'advanced-search',
    nameKey: 'companyName',
    searchInfo: vendorSearchInfo(vendors),
    required: true,
  });
  const vendorContactColumn = () => ({
    ...enumColumn('vendorContactId', vendorContacts, { valueKey: 'contactId', label: FormatPersonName }),
    header: 'Vendor Contact',
    type: 'advanced-search',
    nameKey: 'fullName',
    searchInfo: {
      ...contactSearchInfo,
      searchingRows: contactSearchInfo.searchingRows.map((contact) => ({
        ...contact,
        vendorContactId: contact.contactId,
      })),
      idKey: 'vendorContactId',
    },
  });
  const dateColumn = (accessorKey, header, required = false) => ({
    accessorKey,
    header,
    type: 'date',
    required,
    Cell: ({ cell }) => cell.getValue() ? formatDateForInput(cell.getValue()) : '',
  });
  const columnsByService = {
    destinationServices: [
      vendorColumn('Company'),
      vendorContactColumn(),
      dateColumn('orderedDate', 'Ordered Date', true),
      dateColumn('cancelledDate', 'Cancelled Date'),
      dateColumn('completedDate', 'Completed Date'),
      { accessorKey: 'daysAuthorized', header: 'Days Authorized', type: 'number' },
      { accessorKey: 'daysUtilized', header: 'Days Utilized', type: 'number' },
      { accessorKey: 'serviceNote', header: 'Service Note', type: 'text' },
    ],
    languageTraining: [
      vendorColumn('Vendor (Trainer)'),
      vendorContactColumn(),
      { accessorKey: 'hourlyRate', header: 'Hourly Rate', type: 'text' },
      { accessorKey: 'hoursApproved', header: 'Hours Approved', type: 'text' },
      { accessorKey: 'hoursUsed', header: 'Hours Used', type: 'text' },
      dateColumn('orderedDate', 'Ordered Date'),
    ],
    internationalSchooling: [
      vendorColumn('Vendor (School)'),
      vendorContactColumn(),
      { accessorKey: 'studentAge', header: 'Student Age', type: 'number' },
      {
        ...schoolingLevelColumn('schoolingLevel'),
        header: 'Schooling Level',
        type: 'dropdown-v2',
        options: schoolingLevels,
      },
      { accessorKey: 'tuitionAmount', header: 'Tuition Amount', type: 'money' },
      { accessorKey: 'costOfBooks', header: 'Cost of Books', type: 'money' },
      { accessorKey: 'miscellaneousCost', header: 'Miscellaneous Cost', type: 'money' },
      { accessorKey: 'tuitionPaymentTerms', header: 'Tuition Payment Terms', type: 'text' },
      { accessorKey: 'program', header: 'Program', type: 'text' },
      dateColumn('orderedDate', 'Ordered Date'),
    ],
  };

  const normalizeRow = (field, values) => {
    const row = { ...values };
    for (const column of columnsByService[field]) {
      if (column.type === 'number' || column.type === 'money') {
        const value = row[column.accessorKey];
        const parsed = value === '' || value == null ? null : Number(value);
        if (parsed !== null && (!Number.isFinite(parsed) || (column.type === 'number' && !Number.isInteger(parsed)))) {
          return null;
        }
        row[column.accessorKey] = parsed;
      }
    }
    if (!vendors.some((vendor) => vendor.vendorId === row.vendorId)) return null;
    if (row.vendorContactId && !vendorContacts.some((contact) => contact.contactId === row.vendorContactId)) return null;
    return row;
  };

  const updateRows = async (field, rows) => {
    await setFieldValue(`globalService.${field}`, rows);
    inputContext.onChange();
    inputContext.onBlur();
    return true;
  };

  const summaries = SERVICE_TABS.map((tab) => {
    const rows = globalService[tab.field];
    const getContactId = (values) => familyMembers.find((contact) => contact.id === Number(values.intakeContactId))?.id;

    return {
      tab: tab.order,
      adjustedColumns: [{
        ...enumColumn('intakeContactId', familyMembers),
        header: 'Family Member',
        type: 'dropdown-v2',
        required: true,
        options: familyMembers,
      }, ...columnsByService[tab.field]],
      // The shared table mutates its rows during edits; keep Formik values separate.
      rows: rows.map((row) => ({ ...row })),
      addButtonText: 'Add Record',
      hideEdit: isReadOnly || isSubmitting,
      hideAdd: familyMembers.length === 0,
      createRow: (values) => {
        const intakeContactId = getContactId(values);
        const entry = normalizeRow(tab.field, values);
        if (!intakeContactId || !entry) return false;
        return updateRows(tab.field, [...rows, { ...entry, intakeContactId }]);
      },
      updateRow: (row, values) => {
        const intakeContactId = getContactId(values);
        const updated = normalizeRow(tab.field, values);
        if (!intakeContactId || !updated) return false;
        return updateRows(tab.field, rows.map((entry, index) =>
          index === row.index ? { ...entry, ...updated, intakeContactId } : entry,
        ));
      },
      deleteRow: (row) => updateRows(tab.field, rows.filter((entry, index) => index !== row.index)),
    };
  });

  return (
    <Paper classes={{ root: 'card-root' }} className="mb-2">
      <Typography variant="h4" gutterBottom>Global Destination</Typography>
      {familyMembers.length === 0 && (
        <Typography gutterBottom>Save a family member on the intake form before adding services.</Typography>
      )}
      {vendors.length === 0 && (
        <Typography gutterBottom>No vendors are available.</Typography>
      )}
      <CrudTableCard
        tabs={SERVICE_TABS}
        summaries={summaries}
        numColumns={1}
        getNameOfDeleteRow={(row) => getFamilyMemberName(row.original.intakeContactId)}
      />
    </Paper>
  );
};

GlobalDestinationCard.propTypes = {
  globalService: PropTypes.shape({
    destinationServices: PropTypes.arrayOf(PropTypes.object).isRequired,
    languageTraining: PropTypes.arrayOf(PropTypes.object).isRequired,
    internationalSchooling: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  contacts: PropTypes.arrayOf(PropTypes.object).isRequired,
  vendors: PropTypes.arrayOf(PropTypes.object),
  vendorContacts: PropTypes.arrayOf(PropTypes.object),
  enumerations: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  isReadOnly: PropTypes.bool,
};

GlobalDestinationCard.defaultProps = {
  isReadOnly: false,
};

export default GlobalDestinationCard;
